/**
 * OpenAI SDK v4 Structure Analysis
 * 
 * Deep analysis of OpenAI SDK structure to identify patching points
 * and understand the API surface for interception.
 */

import type { OpenAI } from 'openai'
import { Logger } from '@/utils/logger'

const logger = new Logger('OpenAIAnalyzer')

export interface SDKStructureAnalysis {
  version: string
  clientType: string
  chatCompletionsPath: string[]
  completionsPath: string[]
  embeddingsPath: string[]
  streamsPath: string[]
  methodSignatures: Record<string, unknown>
  patchingPoints: {
    chat: {
      completions: {
        path: string
        method: string
        accessible: boolean
        patchStrategy: 'getter-proxy' | 'method-override' | 'prototype-patch'
      }
    }
    completions: {
      path: string
      method: string
      accessible: boolean
      patchStrategy: 'getter-proxy' | 'method-override' | 'prototype-patch'
    }
    embeddings: {
      path: string
      method: string
      accessible: boolean
      patchStrategy: 'getter-proxy' | 'method-override' | 'prototype-patch'
    }
  }
  streamingSupport: {
    hasAsyncIterators: boolean
    streamMethods: string[]
    chunkStructure: Record<string, unknown>
  }
  compatibility: {
    canPatch: boolean
    riskLevel: 'low' | 'medium' | 'high'
    issues: string[]
    recommendations: string[]
  }
}

export class OpenAISDKAnalyzer {
  private analysisCache = new Map<string, SDKStructureAnalysis>()

  /**
   * Perform comprehensive analysis of an OpenAI client instance
   */
  async analyzeSDKStructure(client: OpenAI): Promise<SDKStructureAnalysis> {
    const version = this.getSDKVersion(client)
    // Create a more unique cache key that includes client structure
    const cacheKey = `${version}-${this.getClientStructureHash(client)}`
    
    if (this.analysisCache.has(cacheKey)) {
      return this.analysisCache.get(cacheKey)!
    }

    logger.info(`Analyzing OpenAI SDK v${version} structure`)

    const analysis: SDKStructureAnalysis = {
      version,
      clientType: this.getClientType(client),
      chatCompletionsPath: this.tracePath(client, 'chat.completions'),
      completionsPath: this.tracePath(client, 'completions'),
      embeddingsPath: this.tracePath(client, 'embeddings'),
      streamsPath: this.tracePath(client, 'chat.completions.create'),
      methodSignatures: this.extractMethodSignatures(client),
      patchingPoints: this.identifyPatchingPoints(client),
      streamingSupport: this.analyzeStreamingSupport(client),
      compatibility: this.assessCompatibility(client, version)
    }

    this.analysisCache.set(cacheKey, analysis)
    logger.info(`SDK analysis complete for v${version}`)
    
    return analysis
  }

  /**
   * Generate a hash of the client structure to avoid cache collisions
   */
  private getClientStructureHash(client: OpenAI): string {
    try {
      const structure = {
        hasChat: !!client.chat,
        hasChatCompletions: !!client.chat?.completions,
        hasChatCompletionsCreate: !!client.chat?.completions?.create,
        hasCompletions: !!client.completions,
        hasEmbeddings: !!client.embeddings,
        clientType: this.getClientType(client)
      }
      return JSON.stringify(structure)
    } catch (error) {
      return 'unknown-structure'
    }
  }

  /**
   * Extract SDK version using multiple detection methods
   */
  private getSDKVersion(client: OpenAI): string {
    try {
      // Method 1: Check package.json (if available)
      const packageInfo = require('openai/package.json')
      if (packageInfo?.version) {
        return packageInfo.version
      }
    } catch (error) {
      // Package.json not accessible
    }

    try {
      // Method 2: Check client constructor or static properties
      if ('version' in client.constructor) {
        return (client.constructor as any).version
      }
    } catch (error) {
      // Constructor version not available
    }

    try {
      // Method 3: Check USER_AGENT or similar headers
      const userAgent = (client as any)._client?.userAgent || (client as any).userAgent
      const versionMatch = userAgent?.match(/openai-node\/(\d+\.\d+\.\d+)/)
      if (versionMatch) {
        return versionMatch[1]
      }
    } catch (error) {
      // User agent not accessible
    }

    // Method 4: Feature detection fallback
    return this.detectVersionByFeatures(client)
  }

  /**
   * Detect version based on API features and structure
   */
  private detectVersionByFeatures(client: OpenAI): string {
    try {
      // Check for v4 specific features
      if (client.chat?.completions?.create && typeof client.chat.completions.create === 'function') {
        const methodStr = client.chat.completions.create.toString()
        if (methodStr.includes('stream') || methodStr.includes('AsyncIterable')) {
          return '4.x.x' // v4 detected
        }
      }
      
      return 'unknown'
    } catch (error) {
      logger.warn('Could not detect OpenAI SDK version via features', error)
      return 'unknown'
    }
  }

  /**
   * Determine the client type and constructor
   */
  private getClientType(client: OpenAI): string {
    return client.constructor.name || 'OpenAI'
  }

  /**
   * Trace the path to a specific API endpoint
   */
  private tracePath(client: OpenAI, path: string): string[] {
    const parts = path.split('.')
    const tracedPath: string[] = []
    let current: any = client

    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        tracedPath.push(part)
        current = current[part]
      } else {
        break
      }
    }

    return tracedPath
  }

  /**
   * Extract method signatures for important endpoints
   */
  private extractMethodSignatures(client: OpenAI): Record<string, unknown> {
    const signatures: Record<string, unknown> = {}

    try {
      // Chat completions
      if (client.chat?.completions?.create) {
        signatures.chatCompletionsCreate = {
          name: client.chat.completions.create.name,
          length: client.chat.completions.create.length,
          toString: client.chat.completions.create.toString().slice(0, 200)
        }
      }

      // Completions
      if (client.completions?.create) {
        signatures.completionsCreate = {
          name: client.completions.create.name,
          length: client.completions.create.length,
          toString: client.completions.create.toString().slice(0, 200)
        }
      }

      // Embeddings
      if (client.embeddings?.create) {
        signatures.embeddingsCreate = {
          name: client.embeddings.create.name,
          length: client.embeddings.create.length,
          toString: client.embeddings.create.toString().slice(0, 200)
        }
      }
    } catch (error) {
      logger.warn('Error extracting method signatures', error)
    }

    return signatures
  }

  /**
   * Identify optimal patching points for each endpoint
   */
  private identifyPatchingPoints(client: OpenAI): SDKStructureAnalysis['patchingPoints'] {
    return {
      chat: {
        completions: this.analyzePatchingPoint(client, 'chat.completions', 'create')
      },
      completions: this.analyzePatchingPoint(client, 'completions', 'create'),
      embeddings: this.analyzePatchingPoint(client, 'embeddings', 'create')
    }
  }

  /**
   * Analyze a specific patching point
   */
  private analyzePatchingPoint(client: OpenAI, resourcePath: string, method: string) {
    const parts = resourcePath.split('.')
    let current: any = client
    let path = ''

    // Navigate to the resource
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        path += path ? `.${part}` : part
        current = current[part]
      } else {
        return {
          path: resourcePath,
          method,
          accessible: false,
          patchStrategy: 'method-override' as const
        }
      }
    }

    // Check if the method exists
    const methodExists = current && typeof current === 'object' && method in current
    const methodCallable = methodExists && typeof current[method] === 'function'

    // Determine optimal patching strategy
    let patchStrategy: 'getter-proxy' | 'method-override' | 'prototype-patch' = 'method-override'

    if (this.canUseGetterProxy(current, method)) {
      patchStrategy = 'getter-proxy'
    } else if (this.canUsePrototypePatch(current, method)) {
      patchStrategy = 'prototype-patch'
    }

    return {
      path: resourcePath,
      method,
      accessible: methodCallable,
      patchStrategy
    }
  }

  /**
   * Check if getter-proxy strategy is viable
   */
  private canUseGetterProxy(resource: any, method: string): boolean {
    try {
      // Check if the method is defined as a getter or can be overridden
      const descriptor = Object.getOwnPropertyDescriptor(resource, method)
      return !descriptor || descriptor.configurable !== false
    } catch (error) {
      return false
    }
  }

  /**
   * Check if prototype patching is viable
   */
  private canUsePrototypePatch(resource: any, method: string): boolean {
    try {
      // Check if we can access and modify the prototype
      const proto = Object.getPrototypeOf(resource)
      return proto && typeof proto === 'object' && method in proto
    } catch (error) {
      return false
    }
  }

  /**
   * Analyze streaming support capabilities
   */
  private analyzeStreamingSupport(client: OpenAI): SDKStructureAnalysis['streamingSupport'] {
    const streamingSupport = {
      hasAsyncIterators: false,
      streamMethods: [] as string[],
      chunkStructure: {}
    }

    try {
      // Check if chat completions supports streaming
      if (client.chat?.completions?.create) {
        streamingSupport.streamMethods.push('chat.completions.create')
        
        // Try to detect async iterator support
        const createMethod = client.chat.completions.create
        const methodStr = createMethod.toString()
        
        if (methodStr.includes('AsyncIterable') || methodStr.includes('Symbol.asyncIterator')) {
          streamingSupport.hasAsyncIterators = true
        }
      }

      // Check completions streaming
      if (client.completions?.create && typeof client.completions.create === 'function') {
        streamingSupport.streamMethods.push('completions.create')
      }

      // Analyze typical chunk structure (based on OpenAI v4 format)
      streamingSupport.chunkStructure = {
        id: 'string',
        object: 'chat.completion.chunk',
        created: 'number',
        model: 'string',
        choices: [{
          index: 'number',
          delta: {
            role: 'string?',
            content: 'string?'
          },
          finish_reason: 'string?'
        }]
      }

    } catch (error) {
      logger.warn('Error analyzing streaming support', error)
    }

    return streamingSupport
  }

  /**
   * Assess overall compatibility and patching viability
   */
  private assessCompatibility(client: OpenAI, version: string): SDKStructureAnalysis['compatibility'] {
    const issues: string[] = []
    const recommendations: string[] = []
    let riskLevel: 'low' | 'medium' | 'high' = 'low'

    // Check version compatibility
    if (version === 'unknown') {
      issues.push('SDK version could not be detected')
      riskLevel = 'medium'
      recommendations.push('Consider version detection improvements')
    }

    // Check critical API access
    const chatCompletionsAccessible = client.chat?.completions?.create && 
                                      typeof client.chat.completions.create === 'function'
    if (!chatCompletionsAccessible) {
      issues.push('chat.completions.create not accessible')
      riskLevel = 'high'
      recommendations.push('Use wrapper-based interception fallback')
    }

    // Check prototype access
    try {
      Object.getPrototypeOf(client)
    } catch (error) {
      issues.push('Prototype access restricted')
      riskLevel = 'medium'
      recommendations.push('Limit to method override patching')
    }

    // Check configurability
    try {
      const descriptor = Object.getOwnPropertyDescriptor(client.chat?.completions, 'create')
      if (descriptor && descriptor.configurable === false) {
        issues.push('Methods are non-configurable')
        riskLevel = 'medium'
        recommendations.push('Use getter-proxy or prototype patching')
      }
    } catch (error) {
      // Descriptor access issues are expected in some environments
    }

    const canPatch = riskLevel !== 'high'


    return {
      canPatch,
      riskLevel,
      issues,
      recommendations
    }
  }

  /**
   * Get cached analysis or return null
   */
  getCachedAnalysis(version: string): SDKStructureAnalysis | null {
    return this.analysisCache.get(version) || null
  }

  /**
   * Clear analysis cache
   */
  clearCache(): void {
    this.analysisCache.clear()
  }
}

// Export singleton instance
export const openaiAnalyzer = new OpenAISDKAnalyzer()